import { Component, Input, Output, EventEmitter, HostBinding, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { WebSocketService } from '../../services/web-socket.service';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NgClass, SlicePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { UsersService } from '../../admin/users/users.service';
import { UserProfile } from '../../admin/users/user.model';
import { SystemApp } from '../../constans/SystemConfigurations';
import { StringUtils } from '../../utils/StringUtils';
import { TranslateModule } from '@ngx-translate/core';
import { SoundAlertService } from '../../services/sound-alert.service';

export interface ChatMessage {
  id?: number;
  user: { id: any; username: string; email: string; avatar: string };
  message: string;
  timestamp: string;
  isNew?: boolean;
  replyTo?: { name: string; text: string };
  reactions?: { emoji: string; count: number; users?: string[]; reacted?: boolean }[];
}

export interface MessageGroup {
  sender: string;          // email
  senderName: string;
  messages: ChatMessage[];
  readBy?: { id: any; username: string }[];
  showUnreadDivider?: boolean;
  unreadCount?: number;
}

// ── Color palette keyed by userId hash ──────────────────────────────────
const PALETTE = [
  { bg: '#dbeafe', fg: '#1e40af' },
  { bg: '#dcfce7', fg: '#166534' },
  { bg: '#fef3c7', fg: '#92400e' },
  { bg: '#ede9fe', fg: '#6d28d9' },
  { bg: '#fce7f3', fg: '#9d174d' },
];

function hashId(id: any): number {
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) { h += s.charCodeAt(i); }
  return h;
}

@Component({
    selector: 'app-chat-room',
    templateUrl: './chat-room.component.html',
    styleUrls: ['./chat-room.component.scss'],
    imports: [FormsModule, NgClass, MatIconModule, TranslateModule, SlicePipe]
})
export class ChatRoomComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() embedded = false;
  @HostBinding('class.is-embedded') get isEmbedded() { return this.embedded; }

  /** Set when the caller already picked a specific room (the multi-room
   * picker) — ngOnInit() connects straight to it via switchRoom() instead
   * of a plain reconnectNow(). Doing both (the picker calling switchRoom()
   * itself, then this component ALSO reconnecting on mount) raced two
   * reconnects back to back and was intermittently leaving the socket on a
   * connection whose chat_history got lost, showing an empty chat. */
  @Input() targetRoomId: number | null = null;

  /** Emitted after a successful delete-for-me, so an embedding parent (e.g.
   * the Adaline widget) can close the chat panel — the room is gone from
   * view, there's nothing left to show. */
  @Output() chatDeleted = new EventEmitter<void>();

  // Captured from chat_history's `room_id` (ChatConsumer always includes it
  // — see chat_app/consumers.py). Drives the delete button in the embedded
  // subheader.
  currentRoomId: number | null = null;

  @ViewChild('messageContainer') private messageContainer!: ElementRef;

  roomName = '1';
  currentUser: UserProfile;
  messages: ChatMessage[] = [];
  messageGroups: MessageGroup[] = [];
  newMessage = '';
  baseUrl = SystemApp.base_url_api;
  typingUsers = new Set<string>();
  typingTimeout: any;
  socketSub!: Subscription;
  showChatBox = false;
  isAtBottom = true;
  replyTarget: { senderName: string; text: string } | null = null;

  unreadCount$ = this.userService.unreadCount$;

  constructor(
      private socketService: WebSocketService,
      private http: HttpClient,
      private userService: UsersService,
      private soundAlertService: SoundAlertService,
      private cdr: ChangeDetectorRef,
  ) {
    this.currentUser = this.userService.getLoginUserDetails();
  }

  ngOnInit(): void {
    // Embedded mode: always (re)connect so the server sends a fresh
    // chat_history — straight to a specific room if one was picked, plain
    // reconnect otherwise. Non-embedded: just ensure connection is open
    // (no-op if already connected).
    if (this.embedded) {
      if (this.targetRoomId != null) {
        this.socketService.switchRoom(this.targetRoomId);
      } else {
        this.socketService.reconnectNow();
      }
    } else {
      this.socketService.connect();
    }
    this.socketSub = this.socketService.messages$.subscribe((data: any) => {
      if (data.type === 'chat_history') {
        if (typeof data.room_id === 'number') {
          this.currentRoomId = data.room_id;
        }
        this.messages = data.messages.map((m: any) => ({
          ...m,
          reactions: this.mapReactions(m.reactions),
        }));
        this.rebuildGroups();
        // Embedded mode: panel slide-up animation is 250ms, so wait longer
        this.scrollToBottom(this.embedded ? 400 : 80);
      }
      if (data.type === 'chat_message') {
        const msg: ChatMessage = {
          ...data,
          isNew: true,
          reactions: this.mapReactions(data.reactions),
        };
        if (this.replyTarget && data.isMine) {
          msg.replyTo = { name: this.replyTarget.senderName, text: this.replyTarget.text };
        }
        this.messages.push(msg);
        this.rebuildGroups();
        if (!this.showChatBox) {
          this.userService.setUnreadCount(this.userService.getUnreadCount() + 1);
          this.soundAlertService.playAlert('chatbox.mp3');
        }
        if (this.isAtBottom) { this.scrollToBottom(); }
      }
      if (data.type === 'reaction_update') {
        const msg = this.messages.find(m => m.id === data.message_id);
        if (msg) {
          msg.reactions = this.mapReactions(data.reactions);
          this.rebuildGroups();
        }
      }
      if (data.type === 'typing') {
        this.typingUsers.add(data.user.username);
        setTimeout(() => this.typingUsers.delete(data.user.username), 2000);
      }
      if (data.type === 'stop_typing') {
        this.typingUsers.delete(data.user.username);
      }

      // ChatbotComponent (the Adaline widget) is OnPush, and this update
      // arrives via WebSocketService — a subscription this component owns
      // directly, entirely outside anything ChatbotComponent's own template
      // bindings would normally notice. Without this, Angular's OnPush gate
      // on that ancestor silently skips re-rendering this whole subtree:
      // `this.messages` above is updated correctly, but nothing shows on
      // screen until some unrelated DOM event (e.g. focusing the message
      // input, which IS bound inside this component's own template) happens
      // to force a check. markForCheck() marks the ancestor chain dirty too,
      // so the very next tick renders it — no interaction required.
      this.cdr.markForCheck();
    });
  }

  // ── Group consecutive messages by sender ──────────────────────────────
  rebuildGroups(): void {
    const groups: MessageGroup[] = [];
    let current: MessageGroup | null = null;

    for (const msg of this.messages) {
      if (!current || current.sender !== msg.user.email) {
        current = {
          sender: msg.user.email,
          senderName: msg.user.username,
          messages: [msg],
          readBy: this.isMe(msg.user.email) ? [] : undefined,
        };
        groups.push(current);
      } else {
        current.messages.push(msg);
      }
    }

    // Demo: mark last 2 "mine" groups as read by others
    const myGroups = groups.filter(g => this.isMe(g.sender));
    if (myGroups.length > 0) {
      myGroups[myGroups.length - 1].readBy = [
        { id: 1, username: 'Sara R.' },
        { id: 2, username: 'Jake K.' },
      ];
    }

    this.messageGroups = groups;
  }

  // ── Bubble radius class ───────────────────────────────────────────────
  getBubbleRadius(isMine: boolean, first: boolean, last: boolean, total: number): string {
    const side = isMine ? 'me' : 'them';
    if (total === 1) { return `r-solo-${side}`; }
    if (first)       { return `r-top-${side}`; }
    if (last)        { return `r-bot-${side}`; }
    return `r-mid-${side}`;
  }

  // ── Helpers ───────────────────────────────────────────────────────────
  isMe(email: string): boolean {
    return email === this.currentUser?.email;
  }

  getInitials(name: string): string {
    if (!name) { return '?'; }
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
  }

  getUserBg(id: any): string  { return PALETTE[hashId(id) % PALETTE.length].bg; }
  getUserFg(id: any): string  { return PALETTE[hashId(id) % PALETTE.length].fg; }

  readonly REACTIONS = ['👍', '👎', '❤️', '😂'];

  // ── Helper: map backend reactions → frontend format ───────────────────
  private mapReactions(raw: any[]): ChatMessage['reactions'] {
    if (!raw?.length) { return []; }
    const email = this.currentUser?.email;
    return raw.map(r => ({
      emoji: r.emoji,
      count: r.count ?? r.users?.length ?? 0,
      users: r.users ?? [],
      reacted: r.users?.includes(email) ?? false,
    }));
  }

  // ── Reactions ─────────────────────────────────────────────────────────
  // Both add and toggle are now a single WebSocket toggle — the server
  // handles add-if-absent / remove-if-present and broadcasts reaction_update.
  addReaction(msg: ChatMessage, emoji: string): void {
    if (!msg.id) { return; }
    this.socketService.send({ type: 'reaction', message_id: msg.id, emoji });
  }

  toggleReaction(msg: ChatMessage, emoji: string): void {
    if (!msg.id) { return; }
    this.socketService.send({ type: 'reaction', message_id: msg.id, emoji });
  }

  // ── Reply ─────────────────────────────────────────────────────────────
  setReply(msg: ChatMessage): void {
    this.replyTarget = { senderName: msg.user.username, text: msg.message };
  }

  clearReply(): void {
    this.replyTarget = null;
  }

  // ── Send ──────────────────────────────────────────────────────────────
  sendMessage(): void {
    if (!this.newMessage.trim()) { return; }
    const payload: any = {
      type: 'chat',
      message: this.newMessage,
      user: this.userService.getLoginUserDetails(),
    };
    if (this.replyTarget) {
      payload.replyTo = { name: this.replyTarget.senderName, text: this.replyTarget.text };
    }
    this.socketService.send(payload);
    this.newMessage = '';
    this.clearReply();
    this.socketService.send({ type: 'stop_typing' });
    this.scrollToBottom();
  }

  onKeypress(): void {
    clearTimeout(this.typingTimeout);
    this.socketService.send({ type: 'typing' });
    this.typingTimeout = setTimeout(() => {
      this.socketService.send({ type: 'stop_typing' });
    }, 2000);
  }

  // ── Scroll ────────────────────────────────────────────────────────────
  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    this.isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  }

  scrollToBottom(delay = 80): void {
    setTimeout(() => {
      if (this.messageContainer) {
        this.messageContainer.nativeElement.scrollTop =
            this.messageContainer.nativeElement.scrollHeight;
        this.isAtBottom = true;
      }
    }, delay);
  }

  ngAfterViewInit(): void {
    // In embedded mode, scroll after the panel animation completes (250ms)
    // in case chat_history already arrived before the view was ready.
    if (this.embedded) {
      setTimeout(() => this.scrollToBottom(), 350);
    }
  }

  // ── Delete chat (delete-for-me only) ────────────────────────────────────
  deletingChat = false;

  deleteCurrentChat(): void {
    if (!this.currentRoomId || this.deletingChat) return;
    if (!confirm('למחוק את השיחה הזו? ההודעות יימחקו רק אצלך.')) return;

    this.deletingChat = true;
    this.http.post<{ status: string }>(
      `${SystemApp.base_url_api}/api/chat/rooms/${this.currentRoomId}/delete-for-me/`, {},
    ).subscribe({
      next: () => {
        this.deletingChat = false;
        this.chatDeleted.emit();
      },
      error: () => {
        this.deletingChat = false;
        alert('מחיקת השיחה נכשלה, נסה שוב');
      },
    });
  }

  // ── Open / Close ──────────────────────────────────────────────────────
  openChatBox(): void {
    this.showChatBox = true;
    this.userService.setUnreadCount(0);
    setTimeout(() => this.scrollToBottom(), 200);
  }

  closeChatBox(): void {
    this.showChatBox = false;
  }

  ngOnDestroy(): void {
    this.socketSub?.unsubscribe();
  }

  protected readonly StringUtils = StringUtils;
}
